import { ValidationProvider, ValidationObserver, extend, localize } from 'vee-validate'
import * as rules from 'vee-validate/dist/rules'
import ja from 'vee-validate/dist/locale/ja.json'
import validator from 'validator'
import { isAfter, parse, isValid } from 'date-fns'

const customJa = {
  ...ja,
  messages: {
    ...ja.messages,
    regex: '{_field_}の入力形式が正しくありません'
  }
}

localize('ja', customJa)

// 全ルールをimport
for (let rule in rules) {
  extend(rule, {
    ...rules[rule]
  })
}

extend('phonenumber', {
  validate: value => {
    return /^\d{9,11}$/.test(value)
  },
  message: '{_field_}はハイフンなしの数字9~11桁で入力してください'
})

extend('required_checkbox', {
  ...rules['required'],
  message: '確認してチェックしてください'
})

extend('password', {
  validate: (value) => {
    return value.length >= 8
  },
  message: 'パスワードは8文字以上で入力してください'
})

extend('credit_card', {
  validate: (value) => {
    return validator.isCreditCard(value)
  },
  message: '{_field_}はハイフンなしの半角数字で入力してください',
})

extend('kana', {
  validate: (value) => {
    const regexp = new RegExp('^[ぁ-んー　]+$')
    return regexp.test(value)
  },
  message: '{_field_}はひらがなで入力してください'
})

extend('credit_card_expiration_date', {
  validate: (value) => {
    try {
      const valueDate = parse(value, 'MM/yy', new Date())
      return isAfter(valueDate, new Date())
    } catch (e) {
      return false
    } 
  },
  message: '{_field_}は月/年の形式で入力してください'
})

extend('valid_date', {
  validate: value => {
    const d = new Date(value)
    return isValid(d)
  },
  message: '{_field_}が正しく入力できているかご確認ください'
})

extend('valid_time', {
  validate: value => {
    const isValidFormat = /(\d{2}):(\d{2})/.test(value)
    if (!isValidFormat) return false
    const time = value.split(':')
    const hour = parseInt(time[0])
    const minute = parseInt(time[1])
    return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59
  },
  message: '時間は半角の数字でHH:mmのフォーマットで入力してください'
})

extend('before_date', {
  params: ['target'],
  validate: (value, { target }) => {
    return value <= target
  },
  message: '開始日は終了日以前の日にちを入力してください'
})

extend('after_date', {
  params: ['target'],
  validate: (value, { target }) => {
    return value >= target
  },
  message: '終了日は開始日以降の日にちを入力してください'
})

extend('activity_before_date', {
  params: ['target'],
  validate: (value, { target }) => {
    return value <= target
  },
  message: '活動日時はノートの終了日以前の日にちを入力してください'
})

extend('activity_after_date', {
  params: ['target'],
  validate: (value, { target }) => {
    return value >= target
  },
  message: '活動日時はノートの開始日以降の日にちを入力してください'
})

extend('activity_before_date_repeat', {
  params: ['target'],
  validate: (value, { target }) => {
    return value <= target
  },
  message: '活動日時をノートの「月」に合わせて選び直してください。ノートを間違った場合は、活動記録をコピーし、正しいノートから作り直してください。'
})

extend('activity_after_date_repeat', {
  params: ['target'],
  validate: (value, { target }) => {
    return value >= target
  },
  message: '活動日時をノートの「月」に合わせて選び直してください。ノートを間違った場合は、活動記録をコピーし、正しいノートから作り直してください。'
})

extend('before_time', {
  params: ['target'],
  validate: (value, { target }) => {
    const targetNum = +target.split(':').join('').padEnd(6, '0')
    const valueNum = +value.split(':').join('').padEnd(6, '0')
    return valueNum < targetNum
  },
  message: '開始時間は終了時間以前を入力してください'
})

extend('after_time', {
  params: ['target'],
  validate: (value, { target }) => {
    const targetNum = +target.split(':').join('').padEnd(6, '0')
    const valueNum = +value.split(':').join('').padEnd(6, '0')
    return valueNum > targetNum
  },
  message: '終了時間は開始時間以降を入力してください'
})

extend('hankaku_number', {
  ...rules['numeric'],
  message: '{_field_}は半角の数字を入力してください'
})

// ファイルがURLで与えられている場合はこのバリデーションは使えないので注意。
extend('file_size', {
  validate: (value) => {
    // ファイルがURLで与えられている場合はバリデーションを通すように
    if (!(value instanceof File)) {
      return true
    }
    const MB = 2 ** 20
    return value.size < 5 * MB
  },
  message: '5MB以上のサイズのファイルはアップロードできません',
})

extend('min_val', {
  params: ['target'],
  validate: (value, { target }) => {
    return Number(value) >= Number(target)
  },
  message: '{_field_}には{target}以上の値を入力してください'
})

extend('max_val', {
  params: ['target'],
  validate: (value, { target }) => {
    return Number(value) <= Number(target)
  },
  message: '{_field_}には{target}以下の値を入力してください'
})

extend('body_pain_required', {
  ...rules['required'],
  message: '{_field_}がある場合は痛みの詳細を入力してください'
})

extend('grade_code_unique', {
  validate: (value, existingGradeCodes) => {
    const existingGradeCodesNumber = existingGradeCodes.map(gradeCode => Number(gradeCode))
    return !existingGradeCodesNumber.includes(Number(value))
  },
  message: 'すでに登録されている学年です。登録した学年に記録を追加する時は、登録した学年の右にある🖊️編集ボタンを押し、一番左下にある「記録する学期を追加」から学期を追加してください'
})

extend('semester_unique', {
  validate: (value, existingSemesters) => {
    // existingSemestersの中にvalueが2つ以上同じものがないかチェック
    const count = existingSemesters.filter(semester => semester === value).length
    return count <= 1
  },
  message: '学期が重複しています'
})

extend('phone_number_unique', {
  validate: (value, otherPhoneNumbers) => {
    return !otherPhoneNumbers.includes(value)
  },
  message: '{_field_}が他の連絡先と重複しています'
})

extend('decimal', {
  params: ['digits'],
  validate: (value, { digits }) => {
    // 空文字列やnull, undefinedはtrueを返す。必須にする必要があればrequiredを使う
    if (value === null || value === undefined || value === '') {
      return true
    }
    // 整数部分は必須。小数点がある場合は小数点以下も必須
    const regex = new RegExp(`^\\d+(\\.\\d{1,${digits}})?$`)
    return regex.test(value)
  },
  message: '{_field_}は小数点以下{digits}桁までの数字を入力してください'
})

extend('required_short_message', {
  ...rules['required'],
  message: '{_field_}は必須です'
})

const install = (Vue) => {
  Vue.component('ValidationProvider', ValidationProvider)
  Vue.component('ValidationObserver', ValidationObserver)
}
export default {
  install
}
